// Copyright 2023 Sorama B.V.

export interface IAs2File {
    metadata: IAs2Metadata;
    thumbnail: number[];
    photoNotes?: string[] | undefined;
    pictures?: string[] | undefined;
    textNote?: string | undefined;
    beams?: string[] | undefined;
    rawSensorData?: string[] | undefined;
}

export interface IAs2Metadata {
    captureMode: As2CaptureMode;
    instrument: IAs2Instrument;
    profile: IAs2Profile;
    tags?: IAs2Tags | undefined;
    leaQ?: IAs2LeakQ | undefined;
    leakQ?: IAs2LeakQ | undefined;
    pdq?: IAs2PDQ | undefined;
    survey: IAs2Survey;
    textNote?: string | undefined;
    photoNotes?: string[] | undefined;
    pictures?: string[] | undefined;
    beams?: string[] | undefined;
    version: number;
}

export enum As2CaptureMode {
    Image = "Image",
    Video = "Video",
    Pdq = "PDQ",
    LeakQ = "LeakQ",
    LeaQ = "LeaQ",
    MecQ = "MecQ",
}

export interface IAs2Instrument {
    firmwareVersion?: string | undefined;
    serialNumber?: string | undefined;
    type: As2DeviceType;
    sensorHead: IAs2SensorHead;
    settings?: IAs2InstrumentSettings | undefined;
    sampleRate: number;
}

export enum As2DeviceType {
    Ii900 = "ii900",
    Ii910 = "ii910",
}

export interface IAs2SensorHead {
    channelCount: number;
    serialNumber: string;
    type: number;
    version: number;
}

export interface IAs2InstrumentSettings {
    frontend?: IAs2InstrumentSettingsFrontend | undefined;
}

export interface IAs2InstrumentSettingsFrontend {
    unitSystem: As2UnitSystem;
}

export enum As2UnitSystem {
    Metric = "Metric",
    Imperial = "Imperial",
}

export interface IAs2Profile {
    id: string;
    name: string;
    settings: IAS2ProfileSettings;
}

export interface IAS2ProfileSettings {
    dbSettings: unknown[];
    frequencyBands: unknown[];
    markers: unknown[];
    palettes: unknown[];
    scale: unknown[];
    operatingConditions?: IAS2ProfileSettingsAs2OperatingConditions[] | undefined;
}

export interface IAS2ProfileSettingsAs2OperatingConditions {
    costsOfElectricity: number;
    costsOfGas: number;
    currency: string;
    operatingConditionsDecimals: number;
    operatingFrequency: As2Frequency;
    operatingHoursPerYear: number;
    operatingPressure: number;
    operatingPressureUnit: string;
    ratioOfPowerToFlowRate: number;
    showCostsPerYear: boolean;
    showLeakQScale: boolean;
    showLeakRate: boolean;
}

export interface IAs2Tags {
    leakType: As2LeakType;
    manualLeakType?: string | undefined;
    gasType: As2GasType;
    manualGasType?: string | undefined;
    location?: string | undefined;
    pressure: number;
    pressureUnit?: string | undefined;
    assetName?: string | undefined;
    assetId?: string | undefined;
    assetType?: string | undefined;
    dischargeType: As2DischargeType;
    manualDischargeType?: string | undefined;
    voltage: number;
    frequency: As2Frequency;
    relativeHumidity: number;
    ambientTemperature: number;
    temperatureUnit?: string | undefined;
    weatherNote?: string | undefined;
    assetCalibrationStatus?: As2AssetStatus;
}

export enum As2LeakType {
    Other = "OtherLeakType",
    QuickConnect = "Quick Connect",
    QuickConnectLeaks = "QuickConnectLeaks",
    ThreadedCoupling = "Threaded Coupling",
    ThreadedCap = "ThreadedCap",
    Hose = "Hose",
    HoleInHose = "RubberHose_Hole",
    OpenEnd = "Open End",
    OpenEnd2 = "OpenEnd",
    Automatic = "Automatic",
}

export enum As2AssetStatus {
    AssetCalibrationStatus_Undetermined = "AssetCalibrationStatus_Undetermined",
    AssetCalibrationStatus_AsFound = "AssetCalibrationStatus_AsFound",
    AssetCalibrationStatus_AsLeft = "AssetCalibrationStatus_AsLeft",
}

export enum As2AssetTypes {
    AssetType_Undetermined = "AssetType_Undetermined",
    AssetType_Blower = "AssetType_Blower",
    AssetType_Boiler = "AssetType_Boiler",
    AssetType_Breaker = "AssetType_Breaker",
    AssetType_Busbar = "AssetType_Busbar",
    AssetType_Chiller = "AssetType_Chiller",
    AssetType_Connector = "AssetType_Connector",
    AssetType_Compressor = "AssetType_Compressor",
    AssetType_Disconnect = "AssetType_Disconnect",
    AssetType_ElectricalPanel = "AssetType_ElectricalPanel",
    AssetType_Fan = "AssetType_Fan",
    AssetType_Fuse = "AssetType_Fuse",
    AssetType_Generator = "AssetType_Generator",
    AssetType_Heater = "AssetType_Heater",
    AssetType_Motor = "AssetType_Motor",
    AssetType_Other = "AssetType_Other",
    AssetType_Priority = "AssetType_Priority",
    AssetType_Pump = "AssetType_Pump",
    AssetType_SteamTrap = "AssetType_SteamTrap",
    AssetType_Switch = "AssetType_Switch",
    AssetType_Transformer = "AssetType_Transformer",
    AssetType_UPS = "AssetType_UPS",
    AssetType_Valve = "AssetType_Valve",
}

export enum As2GasType {
    Air = "Air",
    Argon = "Argon",
    Helium = "Helium",
    Nitrogen = "Nitrogen",
    Hydrogen = "Hydrogen",
    NaturalGas = "NaturalGas",
    Steam = "Steam",
    OtherGasType = "OtherGasType",
}

export enum AssetStatus {
    AsFound = "As Found",
    AsLeft = "As Left",
    Undetermined = "Undetermined",
}

export enum As2DischargeType {
    Corona = "DischargeType_Corona",
    Arcing = "DischargeType_Arcing",
    Tracking = "DischargeType_Tracking",
    Internal = "DischargeType_Internal",
    Other = "DischargeType_Other",
}

export enum As2Frequency {
    DC = "Frequency_DC",
    Hz50 = "Frequency_50Hz",
    Hz60 = "Frequency_60Hz",
    Other = "Frequency_Other",
}

export interface IAs2LeakQ {
    distanceToSource: number;
    distanceUnit: string;
    distanceVariance: number;
    manualDistance: number;
    soundPressureLevelAtSource: number;
    soundPressureLevelAtSensor: number;
    soundPressureLevelUnit: string;
    normalisationDistance: number;
    optimizationFrequencyBand: number[];
    optimizationFrequencyUnit: string;
    leakqScaleAtSource: number;
}

export interface IAs2PDQ {
    distanceToSource: number;
    distanceUnit: string;
    distanceVariance: number;
    manualDistance: number;
    soundPressureLevelAtSource: number;
    soundPressureLevelAtSensor: number;
    soundPressureLevelUnit: string;
    normalisationDistance: number;
    dischargePulsesPerMinute: number;
}

export interface IAs2Survey {
    surveyName: string;
    measurementName: string;
    timestamp: number;
    sequenceNumber: number;
}
export interface ILeakqResult {
    leakRate: number;
    range: LeakResultRange;
    leakType: As2LeakType;
}

export enum LeakResultRange {
    SmallerOrEqual = "SmallerOrEqual",
    InRange = "InRange",
    GreaterOrEqual = "GreaterOrEqual",
}

export interface IPdqResult {
    pdqClassifications?: { [key in keyof typeof PdqClassificationType]?: number } | undefined;
    discharges?: IPdqDischarge[] | undefined;
    mainFrequency: number;
}

export enum PdqClassificationType {
    External = "DischargeType_External",
    Internal = "DischargeType_Internal",
    Tracking = "DischargeType_Tracking",
    Other = "DischargeType_Other",
}

export interface IPdqDischarge {
    phase: number;
    apparatentCharge: number;
}
